/**
 * "Chiedi all'AI" in fondo al pannello assistente: domande sulla conversazione
 * con tutto il contesto CRM (edge function whatsapp-chat-ask). Funziona per una
 * chat WhatsApp o per una conversazione Instagram/Messenger: cambia solo la
 * sorgente (conversationSource.ts). Sulle risposte, azioni facoltative:
 * usarle come bozza del messaggio o farne un Task commerciale.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, Loader2, MessageCircleQuestion, Send, PenLine, ListTodo } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { AiMessageContent } from './AiMessageContent';
import { useOrgAiEnabled, AI_DISABLED_TOOLTIP } from '@/lib/hooks/useOrgAiEnabled';
import { askRequestBody, notesKey, type AskHistoryItem, type ConversationSource } from './conversationSource';

interface AskAiSectionProps {
  /** null = non ancora pronta (es. configurazione WhatsApp in caricamento). */
  source: ConversationSource | null;
  /** Domande pronte da un clic (es. "Proponi una risposta"). */
  quickPrompts?: Array<{ label: string; question: string; hint: string }>;
  /** Mette la risposta nel campo di scrittura, senza inviarla. */
  onUseAsDraft?: (text: string) => void;
  /** Apre la creazione di un Task commerciale partendo dalla risposta. */
  onCreateSalesTask?: (answer: string) => void;
}

export function AskAiSection({ source, quickPrompts, onUseAsDraft, onCreateSalesTask }: AskAiSectionProps) {
  const [messages, setMessages] = useState<AskHistoryItem[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Interruttore IA dell'azienda: spento = domande disattivate.
  const aiEnabled = useOrgAiEnabled();
  const resetKey = source ? notesKey(source) : '';

  // Nuova conversazione: domande e risposte della precedente spariscono.
  useEffect(() => {
    setMessages([]);
    setInput('');
    setError(null);
    setExpanded(false);
  }, [resetKey]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const ask = useCallback(async (raw: string) => {
    const question = raw.trim();
    if (!question || loading || !source || !aiEnabled) return;
    setInput('');
    setError(null);
    setExpanded(true);
    const history = messages;
    setMessages([...history, { role: 'user', content: question }]);
    setLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('whatsapp-chat-ask', {
        body: askRequestBody(source, question, history),
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setMessages((prev) => [...prev, { role: 'assistant', content: data?.answer || '' }]);
    } catch {
      setError('Non sono riuscito a rispondere. Riprova tra poco.');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }, [loading, source, messages, aiEnabled]);

  const actionCls = 'flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/6 rounded transition-colors';

  return (
    <div className={`flex-shrink-0 border-t border-[rgba(55,53,47,0.06)] dark:border-white/6 flex flex-col transition-all duration-200 ${expanded && messages.length > 0 ? 'max-h-[85vh]' : ''}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between px-3.5 py-2 hover:bg-[rgba(55,53,47,0.024)] dark:hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <MessageCircleQuestion className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
          <span className="text-[11px] font-semibold text-[#37352F] dark:text-white/90">Chiedi all'AI</span>
          {messages.length > 0 && (
            <span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-full font-medium">
              {messages.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {messages.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setMessages([]); setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setMessages([]); setError(null); } }}
              className="text-[10px] text-[#6F6E69] hover:text-red-500 transition-colors px-1"
              {...tip('Cancella domande e risposte')}
            >
              Pulisci
            </span>
          )}
          <ChevronRight className={`w-3 h-3 text-[#6F6E69] transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {expanded && messages.length > 0 && (
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 pb-2 space-y-2.5 min-h-0 max-h-[calc(85vh-90px)]">
          {messages.map((msg, i) => (
            <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
              <div className={`max-w-[90%] rounded-lg px-3 py-2 ${
                msg.role === 'user'
                  ? 'bg-purple-600 dark:bg-purple-500 text-white'
                  : 'bg-[rgba(55,53,47,0.04)] dark:bg-white/[0.04] border border-[rgba(55,53,47,0.06)] dark:border-white/[0.06] text-[#37352F] dark:text-white/90'
              }`}>
                <AiMessageContent content={msg.content} isUser={msg.role === 'user'} />
              </div>
              {msg.role === 'assistant' && msg.content && (onUseAsDraft || onCreateSalesTask) && (
                <div className="flex items-center gap-1 mt-0.5">
                  {onUseAsDraft && (
                    <button type="button" onClick={() => onUseAsDraft(msg.content)} className={actionCls} {...tip('Scrive la risposta nel messaggio, senza inviarla: puoi modificarla prima')}>
                      <PenLine className="w-2.5 h-2.5" /> Usa come bozza
                    </button>
                  )}
                  {onCreateSalesTask && (
                    <button type="button" onClick={() => onCreateSalesTask(msg.content)} className={actionCls} {...tip('Crea un Task commerciale sulla scheda del Lead/Cliente partendo da questa risposta')}>
                      <ListTodo className="w-2.5 h-2.5" /> Crea Task commerciale
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[rgba(55,53,47,0.04)] dark:bg-white/[0.04] border border-[rgba(55,53,47,0.06)] dark:border-white/[0.06] rounded-lg px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 text-purple-500 animate-spin" />
                  <span className="text-[10px] text-[#6F6E69]">Sto pensando...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="px-3.5 pb-2.5 pt-1.5">
        {error && <p className="text-[10px] text-red-500 mb-1.5">{error}</p>}
        {quickPrompts && quickPrompts.length > 0 && messages.length === 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {quickPrompts.map((q) => (
              <button
                key={q.label}
                type="button"
                disabled={!source || loading || !aiEnabled}
                onClick={() => void ask(q.question)}
                className="px-2 py-0.5 text-[10px] rounded-full border border-[rgba(55,53,47,0.12)] dark:border-white/10 text-[#37352F] dark:text-white/80 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 disabled:opacity-40"
                {...tip(aiEnabled ? q.hint : AI_DISABLED_TOOLTIP)}
              >
                {q.label}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setExpanded(true)}
            placeholder={messages.length > 0 ? 'Continua la conversazione...' : 'Es: La richiesta X è compresa nell\'offerta?'}
            className="flex-1 text-[11px] h-8 bg-[rgba(55,53,47,0.024)] dark:bg-white/[0.03] border border-[rgba(55,53,47,0.09)] dark:border-white/[0.06] rounded-lg px-2.5 outline-none focus:border-purple-400 dark:focus:border-purple-400 text-[#37352F] dark:text-white/90 placeholder:text-[#6F6E69] transition-colors"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void ask(input);
              }
            }}
          />
          <button
            onClick={() => void ask(input)}
            disabled={!input.trim() || loading || !source || !aiEnabled}
            className="h-8 w-8 flex items-center justify-center rounded-lg bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 disabled:opacity-40 transition-colors flex-shrink-0"
            {...iconTip(aiEnabled ? 'Invia la domanda (Invio)' : AI_DISABLED_TOOLTIP)}
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Send className="w-3.5 h-3.5 text-white" />}
          </button>
        </div>
      </div>
    </div>
  );
}
